// ============================================================================
// inventorySSOT.js — THE single Inventory calculation engine for CostSpace.
// ============================================================================
// Extracted verbatim (2026-08-13) from Store.jsx's proven, live-reconciled
// logic (F-02 Weighted Average Cost, F-06 Material-Specific Stock Status).
// Store.jsx now imports from here instead of defining this logic locally --
// this file is the ONLY place these calculations exist. The new Inventory
// Management screen imports the exact same functions. There is no second
// calculation engine anywhere in the codebase.
//
// Formula (unchanged from Store.jsx, extended with optional Transfer terms):
//   Closing = Opening + Receipts - Issues - Transfer_Out + Transfer_In
// For any project with zero transfers, Transfer_Out = Transfer_In = 0 and
// this reduces to exactly the pre-existing formula -- proven not to alter
// any existing Store.jsx result.
// ============================================================================

export const SYSTEM_DEFAULT_CRITICAL_PCT = 10
export const SYSTEM_DEFAULT_REORDER_PCT = 20

/**
 * Unified material stock status. Identical logic to Store.jsx's
 * getMaterialStockStatus() (F-06) -- copied here as the single source,
 * Store.jsx now calls this function instead of its own local copy.
 */
export function getMaterialStockStatus(item, materialsPolicyMap) {
  const policy = materialsPolicyMap[(item.ref_code || '').trim().toUpperCase()]
  const hasCustomPolicy = policy && policy.threshold_type != null

  if (item.balance < 0) return { status: 'negative', label: 'Negative', policySource: hasCustomPolicy ? 'customized' : 'default' }
  if (item.balance === 0) return { status: 'out', label: 'Out of Stock', policySource: hasCustomPolicy ? 'customized' : 'default' }

  if (hasCustomPolicy && policy.threshold_type === 'quantity') {
    const criticalThreshold = policy.critical_level
    const reorderThreshold = policy.reorder_level
    if (item.balance <= criticalThreshold) return { status: 'critical', label: 'Critical', policySource: 'customized' }
    if (item.balance <= reorderThreshold) return { status: 'low', label: 'Low Stock', policySource: 'customized' }
    return { status: 'in_stock', label: 'In Stock', policySource: 'customized' }
  }

  const critPct = hasCustomPolicy && policy.threshold_type === 'percentage' ? policy.critical_level : SYSTEM_DEFAULT_CRITICAL_PCT
  const reorderPct = hasCustomPolicy && policy.threshold_type === 'percentage' ? policy.reorder_level : SYSTEM_DEFAULT_REORDER_PCT
  const pct = item.received > 0 ? (item.balance / item.received) * 100 : 0
  const source = (hasCustomPolicy && policy.threshold_type === 'percentage') ? 'customized' : 'default'
  if (pct <= critPct) return { status: 'critical', label: 'Critical', policySource: source }
  if (pct <= reorderPct) return { status: 'low', label: 'Low Stock', policySource: source }
  return { status: 'in_stock', label: 'In Stock', policySource: source }
}

/**
 * Builds the materials Stock Policy lookup map (ref_code -> policy row),
 * normalized the same way as every other ref_code comparison in the
 * codebase (F-04: .trim().toUpperCase()).
 */
export function buildMaterialsPolicyMap(materialsMaster) {
  const map = {}
  materialsMaster.forEach(m => { map[(m.material_code || '').trim().toUpperCase()] = m })
  return map
}

/**
 * THE single inventory aggregation. Identical to Store.jsx's `inventory`
 * reduce (F-02/F-04/F-06), extended with two OPTIONAL parameters:
 * transferOutRows / transferInRows. When omitted (as Store.jsx itself does),
 * behavior is byte-for-byte identical to the pre-existing Store.jsx logic --
 * this is what guarantees zero regression to the existing screen.
 *
 * @param {Array} receipts - store_receipts rows for one project
 * @param {Array} issues - store_issues rows for one project
 * @param {Object} materialsPolicyMap - from buildMaterialsPolicyMap()
 * @param {Array} [transferOutLines] - inventory_transfer_lines where this
 *   project is the source AND the parent transfer.status === 'approved'
 *   (that filtering must be done by the caller before passing rows here --
 *   this function does not know about transfer status, keeping it a pure
 *   aggregation function like the rest of this file)
 * @param {Array} [transferInLines] - inventory_transfer_lines where this
 *   project is the destination AND the parent transfer.status === 'approved'
 */
export function buildInventory(receipts, issues, materialsPolicyMap, transferOutLines = [], transferInLines = []) {
  const map = {}
  receipts.forEach(r => {
    const key = (r.ref_code || r.inventory_ref || r.material).trim().toUpperCase()
    if (!map[key]) map[key] = { material: r.material, ref_code: r.ref_code || r.inventory_ref, unit: r.unit, received: 0, issued: 0, receivedValue: 0, transferOut: 0, transferIn: 0 }
    map[key].received += parseFloat(r.qty_received) || 0
    map[key].receivedValue += parseFloat(r.total_exc_vat) || 0
  })
  issues.forEach(i => {
    const key = (i.ref_code || i.inventory_ref || i.material).trim().toUpperCase()
    if (!map[key]) map[key] = { material: i.material, ref_code: i.ref_code || i.inventory_ref, unit: i.unit, received: 0, issued: 0, receivedValue: 0, transferOut: 0, transferIn: 0 }
    map[key].issued += parseFloat(i.qty_issued) || 0
  })
  // Transfer Out: reduces this project's balance, has NO effect on this
  // project's WAC (WAC is a function of what was received, not what left).
  transferOutLines.forEach(l => {
    const key = (l.ref_code || '').trim().toUpperCase()
    if (!map[key]) map[key] = { material: l.material, ref_code: l.ref_code, unit: l.unit, received: 0, issued: 0, receivedValue: 0, transferOut: 0, transferIn: 0 }
    map[key].transferOut += parseFloat(l.qty) || 0
  })
  // Transfer In: increases this project's balance AND participates in this
  // project's WAC exactly like a Receipt would (using source_wac captured
  // at approval time as the "rate" for this incoming quantity) -- per the
  // approved decision: "New WAC = (Existing Qty x Existing WAC + Transfer
  // Qty x Source WAC) / New Qty". Mathematically this is achieved simply by
  // adding to received/receivedValue exactly as a Receipt does; no separate
  // WAC formula exists or is needed.
  transferInLines.forEach(l => {
    const key = (l.ref_code || '').trim().toUpperCase()
    if (!map[key]) map[key] = { material: l.material, ref_code: l.ref_code, unit: l.unit, received: 0, issued: 0, receivedValue: 0, transferOut: 0, transferIn: 0 }
    const qty = parseFloat(l.qty) || 0
    const rate = parseFloat(l.source_wac) || 0
    map[key].received += qty
    map[key].receivedValue += qty * rate
    map[key].transferIn += qty
  })

  return Object.values(map).map(i => {
    const weightedAvgRate = i.received > 0 ? i.receivedValue / i.received : 0
    const balance = i.received - i.issued - i.transferOut
    // Note: i.received already includes transferIn (added above exactly like
    // a Receipt), so this balance formula is NOT double-counting transferIn --
    // it is algebraically identical to:
    //   Opening + Receipts - Issues - TransferOut + TransferIn
    // because "Receipts" here already = (actual receipts + transfer-in qty).
    const withBalance = { ...i, balance, weightedAvgRate, stockValue: balance * weightedAvgRate }
    const statusInfo = getMaterialStockStatus(withBalance, materialsPolicyMap)
    return { ...withBalance, stockStatus: statusInfo.status, stockStatusLabel: statusInfo.label, stockPolicySource: statusInfo.policySource }
  })
}
